import React, { useEffect, useMemo, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Banknote,
  Bus,
  Users,
  Receipt,
  ArrowRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../../components/shared/PageHeader/PageHeader';
import Card from '../../components/shared/Card/Card';
import LoadingSpinner from '../../components/shared/LoadingSpinner/LoadingSpinner';
import StatusBadge from '../../components/shared/StatusBadge/StatusBadge';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import type { Trip, Booking, Parcel, WithdrawalRequest } from '../../types/api';

interface DaySummary {
  total_income?: string;
  total_expenses?: string;
  net_income?: string;
  trip_count?: number;
  passenger_count?: number;
}

interface TrendPoint {
  day: string;
  income: number;
}

const STATUS_COLORS: Record<string, string> = {
  scheduled:  '#0EA5E9',  // sky-500
  boarding:   '#F59E0B',  // amber-500
  in_transit: '#6366F1',  // indigo-500
  completed:  '#10B981',  // emerald-500
  cancelled:  '#F43F5E',  // rose-500
};

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  boarding: 'Boarding',
  in_transit: 'In transit',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const formatKES = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
};

const isoDate = (d: Date) => d.toISOString().slice(0, 10);

const Dashboard: React.FC = () => {
  const [summary, setSummary] = useState<DaySummary | null>(null);
  const [yesterday, setYesterday] = useState<DaySummary | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Today's summary
        const today = await api.get<DaySummary>('/finance/day-summary/').catch(() => ({ data: {} }));

        // Yesterday's summary for delta
        const ydDate = new Date();
        ydDate.setDate(ydDate.getDate() - 1);
        const yd = await api
          .get<DaySummary>(`/finance/day-summary/?date=${isoDate(ydDate)}`)
          .catch(() => ({ data: {} }));

        // 7-day trend (parallel queries)
        const trendRequests: Promise<{ data: DaySummary }>[] = [];
        const trendDates: Date[] = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          trendDates.push(d);
          trendRequests.push(
            api
              .get<DaySummary>(`/finance/day-summary/?date=${isoDate(d)}`)
              .catch(() => ({ data: {} as DaySummary }))
          );
        }
        const trendRes = await Promise.all(trendRequests);
        const trendPoints: TrendPoint[] = trendRes.map((r, i) => ({
          day: trendDates[i].toLocaleDateString(undefined, { weekday: 'short' }),
          income: parseFloat(r.data.total_income ?? '0') || 0,
        }));

        // All trips (for status breakdown)
        const tripRes = await api.get('/trips/').catch(() => ({ data: { results: [] } }));
        const tripList: Trip[] =
          Array.isArray(tripRes.data) ? tripRes.data : tripRes.data.results || [];

        // Recent activity from a few endpoints in parallel
        const [bookingsRes, parcelsRes, withdrawalsRes] = await Promise.all([
          api.get('/bookings/?ordering=-created_at').catch(() => ({ data: { results: [] } })),
          api.get('/parcels/?ordering=-updated_at').catch(() => ({ data: { results: [] } })),
          api.get('/finance/withdrawals/?ordering=-created_at').catch(() => ({ data: { results: [] } })),
        ]);
        const bookings: Booking[] =
          Array.isArray(bookingsRes.data) ? bookingsRes.data : bookingsRes.data.results || [];
        const parcels: Parcel[] =
          Array.isArray(parcelsRes.data) ? parcelsRes.data : parcelsRes.data.results || [];
        const withdrawals: WithdrawalRequest[] =
          Array.isArray(withdrawalsRes.data) ? withdrawalsRes.data : withdrawalsRes.data.results || [];

        const acts = buildActivity(bookings, parcels, withdrawals);

        if (!cancelled) {
          setSummary(today.data);
          setYesterday(yd.data);
          setTrend(trendPoints);
          setTrips(tripList);
          setActivity(acts);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const todayName = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      }),
    []
  );

  const tripStatusBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    trips.forEach((t) => {
      counts[t.status] = (counts[t.status] ?? 0) + 1;
    });
    return Object.entries(counts).map(([status, value]) => ({
      status,
      label: STATUS_LABELS[status] ?? status,
      value,
      color: STATUS_COLORS[status] ?? '#94A3B8',
    }));
  }, [trips]);

  const totalTrips = tripStatusBreakdown.reduce((s, b) => s + b.value, 0);

  if (loading) {
    return (
      <>
        <PageHeader title="Dashboard" description={`Operational snapshot for ${todayName}.`} />
        <LoadingSpinner centered label="Loading dashboard…" />
      </>
    );
  }

  const incomeToday = parseFloat(summary?.total_income ?? '0') || 0;
  const expensesToday = parseFloat(summary?.total_expenses ?? '0') || 0;
  const netToday = parseFloat(summary?.net_income ?? '0') || 0;
  const incomeYd = parseFloat(yesterday?.total_income ?? '0') || 0;
  const expensesYd = parseFloat(yesterday?.total_expenses ?? '0') || 0;

  const incomeDelta = incomeToday - incomeYd;
  const expenseDelta = expensesToday - expensesYd;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={`Operational snapshot for ${todayName}.`}
      />

      {/* Top stat row */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Income today"
          value={`KES ${incomeToday.toLocaleString()}`}
          icon={<Banknote className="h-5 w-5" />}
          tone="income"
          delta={
            incomeYd > 0
              ? {
                  direction: incomeDelta >= 0 ? 'up' : 'down',
                  label: `${incomeDelta >= 0 ? '+' : ''}KES ${incomeDelta.toLocaleString()} vs yesterday`,
                }
              : null
          }
        />
        <StatCard
          label="Expenses today"
          value={`KES ${expensesToday.toLocaleString()}`}
          icon={<Receipt className="h-5 w-5" />}
          tone="expense"
          delta={
            expensesYd > 0
              ? {
                  direction: expenseDelta <= 0 ? 'up' : 'down',
                  label: `${expenseDelta >= 0 ? '+' : ''}KES ${expenseDelta.toLocaleString()} vs yesterday`,
                }
              : null
          }
        />
        <StatCard
          label="Net income"
          value={`KES ${netToday.toLocaleString()}`}
          icon={<TrendingUp className="h-5 w-5" />}
          tone="net"
        />
        <StatCard
          label="Trips today"
          value={summary?.trip_count ?? 0}
          icon={<Bus className="h-5 w-5" />}
          subValue={`${summary?.passenger_count ?? 0} passengers`}
        />
      </div>

      {/* Charts row */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Income trend (2/3 width) */}
        <Card title="Income — last 7 days" className="lg:col-span-2" padded={false}>
          <div className="px-5 pt-2 pb-4">
            {trend.every((p) => p.income === 0) ? (
              <EmptyState
                title="No income recorded yet this week"
                description="Once trips run and bookings are paid, income will show here."
              />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#9F1239" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#9F1239" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="day"
                      stroke="#64748B"
                      tickLine={false}
                      axisLine={false}
                      style={{ fontSize: 12 }}
                    />
                    <YAxis
                      stroke="#64748B"
                      tickLine={false}
                      axisLine={false}
                      width={50}
                      tickFormatter={formatKES}
                      style={{ fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ stroke: '#9F1239', strokeWidth: 1, strokeDasharray: '3 3' }}
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 16px rgba(15,23,42,0.08)',
                        fontSize: 13,
                      }}
                      formatter={(value) => [`KES ${Number(value).toLocaleString()}`, 'Income']}
                    />
                    <Area
                      type="monotone"
                      dataKey="income"
                      stroke="#9F1239"
                      strokeWidth={2.5}
                      fill="url(#incomeFill)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        {/* Trip status donut */}
        <Card title="Trip status" description={`${totalTrips} trips total`}>
          {totalTrips === 0 ? (
            <EmptyState title="No trips yet" />
          ) : (
            <div className="flex flex-col items-center">
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={tripStatusBreakdown}
                      dataKey="value"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {tripStatusBreakdown.map((entry) => (
                        <Cell key={entry.status} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 16px rgba(15,23,42,0.08)',
                        fontSize: 13,
                      }}
                      formatter={(value, _name, ctx: any) => [
                        `${value} trips`,
                        ctx?.payload?.label,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-2 grid w-full grid-cols-1 gap-1 text-sm">
                {tripStatusBreakdown.map((b) => (
                  <li
                    key={b.status}
                    className="flex items-center justify-between gap-2 px-1"
                  >
                    <span className="flex items-center gap-2 text-slate-700">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: b.color }}
                        aria-hidden
                      />
                      {b.label}
                    </span>
                    <span className="font-semibold tabular-nums text-slate-900">
                      {b.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      {/* Recent activity */}
      <Card
        title="Recent activity"
        description="Latest bookings, parcels, and withdrawals."
      >
        {activity.length === 0 ? (
          <EmptyState
            title="No recent activity"
            description="As bookings, parcels, and withdrawals come in, they'll show here."
          />
        ) : (
          <ul className="divide-y divide-slate-100 -my-3">
            {activity.slice(0, 12).map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 py-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${item.iconBg} ${item.iconColor}`}
                    aria-hidden
                  >
                    {item.icon}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {item.title}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(item.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
                {item.amount != null && (
                  <span className="text-sm font-semibold tabular-nums text-slate-900">
                    KES {item.amount.toLocaleString()}
                  </span>
                )}
                {item.badge}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-3 text-sm">
        <Link
          to="/admin/operations/bookings"
          className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline"
        >
          See all bookings <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
        <Link
          to="/admin/finance"
          className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline"
        >
          Open finance <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
    </>
  );
};

interface ActivityItem {
  id: string;
  title: string;
  timestamp: string;
  amount?: number | null;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  badge?: React.ReactNode;
}

function buildActivity(
  bookings: Booking[],
  parcels: Parcel[],
  withdrawals: WithdrawalRequest[]
): ActivityItem[] {
  const out: ActivityItem[] = [];

  bookings.slice(0, 8).forEach((b) => {
    out.push({
      id: `b-${b.id}`,
      title: `Booking ${b.booking_reference} — ${b.passenger_name}`,
      timestamp: b.created_at,
      amount: parseFloat(b.amount) || null,
      icon: <Users className="h-4 w-4" />,
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-700',
      badge: <StatusBadge variant="booking" status={b.payment_status} />,
    });
  });

  parcels.slice(0, 8).forEach((p) => {
    out.push({
      id: `p-${p.id}`,
      title: `Parcel ${p.tracking_code} — ${p.recipient_name}`,
      timestamp: p.updated_at ?? p.created_at,
      amount: parseFloat(p.shipping_fee) || null,
      icon: <Bus className="h-4 w-4" />,
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-700',
      badge: <StatusBadge variant="parcel" status={p.status} />,
    });
  });

  withdrawals.slice(0, 8).forEach((w) => {
    out.push({
      id: `w-${w.id}`,
      title: `Withdrawal request — wallet #${w.wallet}`,
      timestamp: w.created_at,
      amount: parseFloat(w.amount) || null,
      icon: <Banknote className="h-4 w-4" />,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-700',
      badge: <StatusBadge variant="withdrawal" status={w.status} />,
    });
  });

  out.sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''));
  return out;
}

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  subValue?: string;
  icon?: React.ReactNode;
  tone?: 'default' | 'income' | 'expense' | 'net';
  delta?: {
    direction: 'up' | 'down';
    label: string;
  } | null;
}

const TONE: Record<NonNullable<StatCardProps['tone']>, string> = {
  default: 'text-slate-900',
  income:  'text-emerald-700',
  expense: 'text-rose-700',
  net:     'text-brand-700',
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subValue,
  icon,
  tone = 'default',
  delta,
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-sm font-medium text-slate-600">{label}</p>
      {icon && (
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          {icon}
        </span>
      )}
    </div>
    <p className={`mt-2 text-2xl font-bold tracking-tight tabular-nums ${TONE[tone]}`}>
      {value}
    </p>
    {subValue && (
      <p className="mt-0.5 text-sm text-slate-500">{subValue}</p>
    )}
    {delta && (
      <div className="mt-2 flex items-center gap-1 text-xs font-semibold">
        {delta.direction === 'up' ? (
          <TrendingUp className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
        ) : (
          <TrendingDown className="h-3.5 w-3.5 text-rose-600" aria-hidden />
        )}
        <span
          className={delta.direction === 'up' ? 'text-emerald-700' : 'text-rose-700'}
        >
          {delta.label}
        </span>
      </div>
    )}
  </div>
);

export default Dashboard;
